/**
 * tourWalkability — pure, synchronous geometry for the walking-tour editor.
 *
 * All distances are STRAIGHT-LINE ("as-the-crow-flies") estimates — there is no
 * Google Directions call here (keeps the editor free of metered API cost). The
 * haversine + walking-time formulas mirror the server's `server/utils/geoUtils.js`
 * (`estimateWalkingTime = ceil(m / 83)`, ~5km/h) so live previews in the editor
 * match the values the server stores on save.
 *
 * Stop coordinate shape is tolerant: stops may carry `coordinates: {lat,lng}`
 * (service shape) OR top-level `lat`/`lng` (detail-page shape). Use `coordsOf`.
 */

import { formatDistance } from './distanceFormat';

const EARTH_RADIUS_M = 6371000;

const WALK_METERS_PER_MIN = 83; // ~5 km/h, matches server estimateWalkingTime

const toRad = (deg) => deg * (Math.PI / 180);

/** Read a stop's coordinates tolerantly. Returns {lat,lng} or null. */
export function coordsOf(stop) {
  if (!stop) return null;
  const lat = stop.coordinates?.lat ?? stop.lat;
  const lng = stop.coordinates?.lng ?? stop.lng;
  // Number.isFinite rejects NaN/Infinity too (typeof NaN === 'number' would slip
  // through a bare typeof check and NaN-poison every distance/total downstream).
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

/** Haversine distance in meters between two {lat,lng} points. */
export function distanceMeters(a, b) {
  if (!a || !b) return null;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
  return EARTH_RADIUS_M * c;
}

/** Estimate walking minutes for a straight-line distance (matches server). */
export function estimateWalkingMinutes(meters) {
  if (!meters || meters <= 0) return 0;
  return Math.ceil(meters / WALK_METERS_PER_MIN);
}

/**
 * Per-leg distances between consecutive stops. A leg whose endpoints lack
 * coordinates contributes `meters: null` (excluded from totals).
 * @returns Array<{ fromIndex, toIndex, meters: number|null, minutes: number }>
 */
export function legDistances(stops) {
  const legs = [];
  for (let i = 0; i < stops.length - 1; i++) {
    const a = coordsOf(stops[i]);
    const b = coordsOf(stops[i + 1]);
    const meters = a && b ? distanceMeters(a, b) : null;
    legs.push({
      fromIndex: i,
      toIndex: i + 1,
      meters,
      minutes: meters != null ? estimateWalkingMinutes(meters) : 0,
    });
  }
  return legs;
}

/** Total straight-line route distance (meters). Null legs are skipped. */
export function totalDistance(stops) {
  return legDistances(stops).reduce((sum, leg) => sum + (leg.meters || 0), 0);
}

/** Total estimated walking minutes across the route. */
export function totalWalkingMinutes(stops) {
  return estimateWalkingMinutes(totalDistance(stops));
}

/**
 * Route cost for optimization. For a loop tour the walker returns to the start,
 * so the closing leg (last → first) is part of the real distance and must be
 * included in the objective — otherwise the optimizer minimizes the wrong path.
 */
function routeCost(stops, isLoop) {
  let cost = totalDistance(stops);
  if (isLoop && stops.length > 1) {
    const first = coordsOf(stops[0]);
    const last = coordsOf(stops[stops.length - 1]);
    if (first && last) cost += distanceMeters(last, first);
  }
  return cost;
}

/** Swap two indices in a copied array (immutable helper). */
function withSwapped(arr, i, j) {
  const next = arr.slice();
  [next[i], next[j]] = [next[j], next[i]];
  return next;
}

/**
 * Zig-zag detector. For each adjacent pair (i, i+1), test whether swapping them
 * shortens the WHOLE route past both thresholds (absolute + relative). Catches
 * the common "one stop out of sequence between two others" backtrack and reports
 * the precise fix.
 *
 * @returns Array<{ index, savedMeters, suggestSwapWith }> — `index` is the stop
 *   to flag (the later of the pair), `suggestSwapWith` is its neighbor.
 */
export function adjacentSwapFlags(stops, { minMeters = 50, minPct = 0.1 } = {}) {
  const flags = [];
  const current = totalDistance(stops);
  if (current <= 0 || stops.length < 3) return flags;

  for (let i = 0; i < stops.length - 1; i++) {
    // Need both endpoints located to reason about a swap.
    if (!coordsOf(stops[i]) || !coordsOf(stops[i + 1])) continue;
    const swapped = totalDistance(withSwapped(stops, i, i + 1));
    const saved = current - swapped;
    if (saved >= minMeters && saved / current >= minPct) {
      flags.push({ index: i + 1, savedMeters: Math.round(saved), suggestSwapWith: i });
    }
  }
  return flags;
}

/**
 * Advisory route optimizer. Pins the start and/or end stop (user-controlled
 * bookends) and any coordinate-less stop in place; reorders only the movable
 * middle via nearest-neighbor seed + 2-opt refinement. NEVER applies itself —
 * the caller previews the result and the user accepts/rejects.
 *
 * @returns {{
 *   order: Array,            // proposed reordered stops
 *   beforeMeters, afterMeters, improvedPct,
 *   movableCount,            // how many stops were eligible to move
 *   farLegs: Array<{ index, meters }>  // legs over farLegMeters in the proposal
 * }}
 */
export function optimizeOrder(stops, { pinStart = true, pinEnd = true, farLegMeters = 2000, isLoop = false } = {}) {
  const n = stops.length;
  // A loop returns to the start, so the "end" should be free to land wherever
  // makes the full cycle shortest — pinning it would fight the loop. Pin start
  // only, and include the closing leg in the cost.
  const effectivePinEnd = isLoop ? false : pinEnd;
  const cost = (s) => routeCost(s, isLoop);

  const beforeMeters = cost(stops);
  const base = {
    order: stops,
    beforeMeters,
    afterMeters: beforeMeters,
    improvedPct: 0,
    movableCount: 0,
    farLegs: [],
  };
  if (n < 3) return base;

  // Indices that must keep their position: pinned bookends + any without coords.
  const fixed = new Set();
  if (pinStart) fixed.add(0);
  if (effectivePinEnd) fixed.add(n - 1);
  stops.forEach((s, i) => { if (!coordsOf(s)) fixed.add(i); });

  const movableIndices = [];
  for (let i = 0; i < n; i++) if (!fixed.has(i)) movableIndices.push(i);
  if (movableIndices.length < 2) return base; // nothing meaningful to reorder

  // Anchor for nearest-neighbor: the last fixed stop before the movable block,
  // else the first stop with coordinates.
  const anchorIndex = pinStart ? 0 : (movableIndices[0] - 1 >= 0 ? movableIndices[0] - 1 : 0);
  const anchorCoord = coordsOf(stops[anchorIndex]) || coordsOf(stops[movableIndices[0]]);

  // --- Nearest-neighbor seed over the movable stops ---
  const remaining = movableIndices.slice();
  const seededOrder = [];
  let cursor = anchorCoord;
  while (remaining.length) {
    let bestPos = 0;
    let bestDist = Infinity;
    for (let k = 0; k < remaining.length; k++) {
      const c = coordsOf(stops[remaining[k]]);
      const d = c && cursor ? distanceMeters(cursor, c) : Infinity;
      if (d < bestDist) { bestDist = d; bestPos = k; }
    }
    const chosen = remaining.splice(bestPos, 1)[0];
    seededOrder.push(chosen);
    cursor = coordsOf(stops[chosen]) || cursor;
  }

  // Build a full index order (fixed stops keep their slots, movable slots filled
  // in seeded order).
  const assemble = (movableOrder) => {
    const result = new Array(n);
    fixed.forEach((i) => { result[i] = i; });
    let m = 0;
    for (let i = 0; i < n; i++) {
      if (!fixed.has(i)) result[i] = movableOrder[m++];
    }
    return result.map((idx) => stops[idx]);
  };

  // --- 2-opt refinement on the movable subsequence ---
  // Operate on the movable order; reverse segments while the assembled total shortens.
  let movableOrder = seededOrder;
  let bestTotal = cost(assemble(movableOrder));
  let improved = true;
  let iterations = 0;
  const MAX_ITER = 1000;
  while (improved && iterations < MAX_ITER) {
    improved = false;
    for (let a = 0; a < movableOrder.length - 1; a++) {
      for (let b = a + 1; b < movableOrder.length; b++) {
        const candidate = movableOrder.slice();
        // reverse segment [a..b]
        let lo = a, hi = b;
        while (lo < hi) { [candidate[lo], candidate[hi]] = [candidate[hi], candidate[lo]]; lo++; hi--; }
        const t = cost(assemble(candidate));
        if (t < bestTotal - 0.5) { // tiny epsilon to avoid float churn
          movableOrder = candidate;
          bestTotal = t;
          improved = true;
        }
        iterations++;
        if (iterations >= MAX_ITER) break;
      }
      if (iterations >= MAX_ITER) break;
    }
  }

  const order = assemble(movableOrder);
  const afterMeters = bestTotal;
  const improvedPct = beforeMeters > 0 ? (beforeMeters - afterMeters) / beforeMeters : 0;

  // Flag any leg in the PROPOSED order that's too far to reasonably walk.
  const farLegs = legDistances(order)
    .filter((leg) => leg.meters != null && leg.meters > farLegMeters)
    .map((leg) => ({ index: leg.toIndex, meters: Math.round(leg.meters) }));
  // For a loop, the return-to-start leg is part of the walk too — check it.
  if (isLoop && order.length > 1) {
    const first = coordsOf(order[0]);
    const last = coordsOf(order[order.length - 1]);
    if (first && last) {
      const closing = distanceMeters(last, first);
      if (closing > farLegMeters) farLegs.push({ index: 0, meters: Math.round(closing) });
    }
  }

  return {
    order,
    beforeMeters: Math.round(beforeMeters),
    afterMeters: Math.round(afterMeters),
    improvedPct,
    movableCount: movableIndices.length,
    farLegs,
  };
}

/** Format meters for display ("271 m" / "1.2 km", or ft/mi). Delegates to
 * the shared distance formatter; pass the user's unit from useDistanceUnit(). */
export function formatMeters(meters, unit = 'metric') {
  if (!meters) return unit === 'imperial' ? '0 ft' : '0 m';
  return formatDistance(meters, unit);
}

/** Format minutes for display ("5 min" / "1h 5min"). */
export function formatMinutes(minutes) {
  if (!minutes) return '0 min';
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}min` : `${h}h`;
}
